import GD from '../GD'
import { DCCAPI, Services } from '../utils'
import { CHUNK_UPLOAD_TRIGGER_SIZE } from '../enums'
import { validateFileUploadCriteria } from '../utils/Validation'
import { Config } from './index'

class Images {
  private authKey = 'web'

  constructor() {
    GD.AUTH_COMPLETE.subscribe(data => {
      this.authKey = data.key
    })

    GD.UPLOAD_IMAGE.listener = async ({ file, tempUID }): Promise<boolean> => {
      return this.uploadImage({ file, tempUID })
    }

    GD.FETCH_IMAGE.listener = async ({ thumb, imageUID }): Promise<null | string> => {
      const chat = await GD.GET_CHAT.request()
      return this.fetchImage({ thumb, imageUID, key: chat.securityKey })
    }

    GD.DOWNLOAD_IMAGE.subscribe(async req => {
      await this.onImageDownloadRequest(req)
    })

    GD.SERVICE_READY.invoke('images')
  }

  private async onImageDownloadRequest(req: { uid: string, name: string, chatUID: string }) {
    const chat = await GD.GET_CHAT.request()

    const b64: string | null = await this.fetchImage({
      thumb: false,
      imageUID: req.uid,
      key: chat.securityKey,
    })

    if (!b64) {
      GD.LOGE.invoke('Can\'t download image')
      return
    }

    const downloadLink = document.createElement('a')
    document.body.appendChild(downloadLink)
    downloadLink.href = b64
    downloadLink.target = '_self'
    downloadLink.download = req.name
    downloadLink.click()
    downloadLink.remove()
  }

  private async fetchImage(req: { thumb?: boolean, imageUID: string, key: string }): Promise<null | string> {
    const xhr = new XMLHttpRequest()

    function arrayBufferToBase64(arrayBuffer) {
      let binaryString = ''
      for (let i = 0; i < arrayBuffer.length; i++) {
        binaryString += String.fromCharCode(arrayBuffer[i])
      }
      return btoa(binaryString)
    }

    if (!this.authKey) {
      GD.LOGE.invoke('No auth key, can\'t load images')
      return null
    }

    let url: string = window.DCC_CONFIG.API_FILES_URL + 'uid=' + req.imageUID + '&key=' + this.authKey
    if (req.thumb) url += '&thumb=1'

    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        const uint8Array = new Uint8Array(xhr.response)
        const imgOrUint8Array = Services.imagePacker.decrypt(uint8Array, req.key)

        if (imgOrUint8Array instanceof Uint8Array) {
          resolve(`data:image/jpeg;base64,${arrayBufferToBase64(uint8Array)}`)
        }
        else if (imgOrUint8Array) {
          resolve(`data:image/jpeg;base64,${btoa(imgOrUint8Array)}`)
        }
        else {
          reject('Data is encrypted, but decryption failed')
        }
      }

      xhr.onerror = () => {
        return null 
      }
      xhr.responseType = 'arraybuffer'
      xhr.open('get', url, true)
      xhr.send()
    })
  }

  private async uploadImage({ file, tempUID }): Promise<boolean> {
    const chat: ChatVO = await GD.GET_CHAT.request()

    return new Promise((resolve, reject) => {
      const fileReader: FileReader = new FileReader()

      fileReader.onload = async (event) => {
        const img = new Image()
        img.onload = async () => {
          const width = img.width
          const height = img.height

          const isCriteriasPassed = await validateFileUploadCriteria({
            imgW: width,
            imgH: height,
            imgSize: file.size,
          })

          if (!isCriteriasPassed) {
            GD.ALERT_ERROR.invoke('Can\'t upload file: ' + file.name + ', size too big')
            return resolve(false)
          }

          const response = await this.doImageUpload({
            fileName: file.name,
            b64: fileReader.result as string,
            chatUID: chat.uid,
            shouldUseChunks: CHUNK_UPLOAD_TRIGGER_SIZE < file.size,
            file: file,
            tempUID: tempUID,
          })

          if (response) resolve(true)
        }

        img.onerror = reject
        img.src = event.target?.result as string
      }

      fileReader.onerror = reject
      fileReader.readAsDataURL(file)
    })
  }

  private async doImageUpload(
    req: {
      fileName: string
      b64: string
      chatUID: string
      owner_uid?: string
      shouldUseChunks: boolean
      file: File
      tempUID: string
    }): Promise<null | boolean> {
    const tmpImg: HTMLImageElement = document.createElement('img')

    return new Promise((resolve, reject) => {
      tmpImg.onload = async () => {
        const chat = await GD.GET_CHAT.request()

        // upload via chunks //
        if (req.shouldUseChunks) {
          const uploadResp = await Services.chunkUploader.startUploadViaChunks({
            file: req.file,
            owner_uid: req.owner_uid,
            tempUID: req.tempUID
          })

          if (uploadResp) return resolve(uploadResp)
          else resolve(null)
        }
        // --------------- //

        const images: string[] | null = Services.imagePacker.get2ImagesForSend(tmpImg, chat.securityKey)
        if (!images) {
          return resolve(false)
        }

        GD.FILE_PROGRESS_UPDATE.invoke({ tempUID: req.tempUID, progress: 50 })
        const title = req.fileName
        const resp = await GD.REQ_HTTP.request({
          url: window.DCC_CONFIG.API_FILES_URL,
          method: 'files.addImage',
          chatUID: req.chatUID,
          image: images[0],
          thumb: images[1],
          title: title,
          crypted: '1',
          b64: '1'
        })

        if (resp.error || resp.data == null) {
          GD.ALERT_ERROR.invoke('Can\'t upload image: ' + resp.errorMsg)
          return resolve(false)
        }

        GD.FILE_PROGRESS_UPDATE.invoke({ tempUID: req.tempUID, progress: 100 })
        this.informAboutPictureUpload(resp, req)
        resolve(true)
      }

      tmpImg.onerror = (e) => {
        console.error('Error loading image:', e)
        reject(e)
      }

      tmpImg.src = req.b64
    })
  }

  private async informAboutPictureUpload(resp, req) {
    const chat = await GD.GET_CHAT.request()

    const { name, uid } = resp.data as IObjectData
    GD.WS_SEND.invoke({
      method: 'msgAdd',
      data: {
        chatUID: req.chatUID,
        text: DCCAPI.pack(
          Config.systemSequence +
          JSON.stringify({
            method: 'fileSended',
            title: name,
            type: 'file',
            fileType: 'cimg',
            additionalData: uid
          }),
          chat.securityKey,
        )
      }
    })
  }
}

export default Images
